import {
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { magnifyValue, STOCK_UI } from '@diamond/shared/decimal-util-web';
import {
  CartTotalBody,
  CartTotalWithMagnify,
  DetailProduct,
} from '@diamond/shared/types';
import { Button, Icon, showToast } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  convertToFloat,
  getItemCardTrackingParams,
  isEven,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useStockNumberInput } from '@diamond/sol-buyer/utils';
import {
  ContactSalesModal,
  QuantityControl,
} from '@diamond/sol-buyer-components';
import { useCartStore } from '@diamond/sol-buyer-context';
import { Add, Favorite, FavoriteBorder } from '@mui/icons-material';
import { dinero, toDecimal } from 'dinero.js';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface OrderQuantityCounterProps {
  productId: string;
  step: number;
  minimumValue: number;
  precision: number;
  stock: number;
  stockStatus: string;
  wishlistId: string;
  handleAddToTemplate: (quantity: number) => void;
  handleAddToCart: (quantity: number) => void;
  handleAddToWishlist?: () => void;
  setHasActiveDiscount?: (hasActiveDiscount: boolean) => void;
  setProduct: (product: CartTotalBody['products'][number]) => void;
  cartTotalData?: CartTotalWithMagnify;
  isCartTotalFetching?: boolean;
  unitPrice?: number;
  productData?: DetailProduct;
  /** When true, disables cart functionality for F&B Equipment */
  isFnBEquipment?: boolean;
}

export const OrderQuantityCounter = ({
  productId,
  step,
  minimumValue,
  precision,
  stock,
  stockStatus,
  wishlistId,
  handleAddToTemplate,
  handleAddToCart,
  handleAddToWishlist,
  setHasActiveDiscount,
  setProduct,
  cartTotalData,
  isCartTotalFetching,
  unitPrice,
  productData,
  isFnBEquipment,
}: OrderQuantityCounterProps) => {
  const [available, setAvailable] = useState<
    'Available' | 'Out of Stock' | 'Stok Terbatas' | 'Produk Belum Tersedia'
  >('Out of Stock');
  const [statusColor, setStatusColor] = useState<
    'azure' | 'red' | 'orange' | 'red'
  >('red');
  const [uiQuantity, setUiQuantity] = useState<number>(0);
  const toast = useToast();

  const orderQtyMultiple = useMemo(
    () =>
      Number(
        toDecimal(
          dinero({
            amount: step,
            currency: STOCK_UI,
          })
        )
      ),
    [step]
  );

  const minOrderQty = useMemo(
    () =>
      Number(
        toDecimal(
          dinero({
            amount: minimumValue,
            currency: STOCK_UI,
          })
        )
      ),
    [minimumValue]
  );

  const maxStock = useMemo(
    () =>
      Number(
        toDecimal(
          dinero({
            amount: stock,
            currency: STOCK_UI,
          })
        )
      ),
    [stock]
  );

  const maxStockGanjil = useMemo(
    () =>
      Number(
        (Math.floor(maxStock / orderQtyMultiple) * orderQtyMultiple).toFixed(2)
      ),
    [maxStock, orderQtyMultiple]
  );

  const maxStockGenap = useMemo(
    () =>
      Number(
        (Math.round(maxStock / orderQtyMultiple) * orderQtyMultiple).toFixed(2)
      ),
    [maxStock, orderQtyMultiple]
  );

  const maxAllowedQuantity = useMemo(
    () =>
      isEven(orderQtyMultiple) || orderQtyMultiple === 1
        ? maxStockGanjil
        : maxStockGenap,
    [isEven, orderQtyMultiple, maxStockGanjil, maxStockGenap]
  );

  const {
    inputProps,
    incProps,
    decProps,
    hasFractionalStep,
    getCurrentUiValue,
  } = useStockNumberInput({
    id: `input-qty-desktop-${productId}`,
    name: `input-qty-desktop-${productId}`,
    valueUi: uiQuantity || minOrderQty,
    stepUi: orderQtyMultiple,
    minUi: minOrderQty,
    maxUi: maxAllowedQuantity,
    stepMagnified: step,
    roundOnBlurOnly: true,
    isDisabled: minOrderQty > maxStock || stockStatus === 'OUT_OF_STOCK',
    onChangeMagnifiedImmediate: (magnified) => {
      setUiQuantity(magnified / 1000);
      setProduct({
        product_id: productId,
        quantity: magnified,
      });
    },
    onChangeMagnified: (magnified) => {
      setUiQuantity(magnified / 1000);
      setProduct({
        product_id: productId,
        quantity: magnified,
      });
    },
    onError: (errorMessage) => {
      showToast(toast, 'error', errorMessage);
    },
  });

  const count = getCurrentUiValue().toString();

  useEffect(() => {
    setProduct({
      product_id: productId,
      quantity: minimumValue,
    });
  }, [productId, minimumValue, setProduct]);

  const setCompiledPromo = useCartStore((state) => state.setCompiledPromo);
  const resetCart = useCartStore((state) => state.reset);

  const handleCartPromoUpdate = useCallback(() => {
    if (cartTotalData?.promos) {
      setCompiledPromo(cartTotalData.promos);
    }
  }, [cartTotalData?.promos, setCompiledPromo]);

  useEffect(() => {
    handleCartPromoUpdate();
    return () => {
      resetCart([]);
    };
  }, [handleCartPromoUpdate, resetCart]);

  const isPriceZero = unitPrice === 0;
  // Only out of stock is truly unavailable. Price zero shows contact sales flow.
  const isOutOfStock = maxStock <= 0;

  const availableStatus = useMemo(() => {
    const available:
      | 'Available'
      | 'Out of Stock'
      | 'Stok Terbatas'
      | 'Produk Belum Tersedia' = isPriceZero
      ? 'Produk Belum Tersedia'
      : maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'Available'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'Stok Terbatas'
      : 'Out of Stock';

    const color: 'azure' | 'red' | 'orange' = isPriceZero
      ? 'red'
      : maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'azure'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'orange'
      : 'red';

    return { available, color };
  }, [maxStock, stockStatus, isPriceZero]);

  useEffect(() => {
    setAvailable(availableStatus.available);
    setStatusColor(availableStatus.color);
  }, [availableStatus]);

  const isMagnifiedPriceDiff = useMemo(
    () =>
      cartTotalData?.originalPriceMagnify?.magnifiedValue !==
      cartTotalData?.finalPriceMagnify?.magnifiedValue,
    [
      cartTotalData?.originalPriceMagnify?.magnifiedValue,
      cartTotalData?.finalPriceMagnify?.magnifiedValue,
    ]
  );

  useEffect(() => {
    setHasActiveDiscount?.(isMagnifiedPriceDiff);
  }, [isMagnifiedPriceDiff, setHasActiveDiscount]);

  // Contact Sales Modal for price zero products
  const {
    isOpen: isContactSalesOpen,
    onOpen: onOpenContactSales,
    onClose: onCloseContactSales,
  } = useDisclosure();

  const handleContactSalesClick = () => {
    trackEvent(TRACKING_EVENTS.CONTACT_SALES_CTA_CLICK, {
      product_id: productId,
      source: 'detail',
    });
    if (productData) {
      trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
        ...getItemCardTrackingParams(
          productData,
          1,
          TRACKING_PAGES.PRODUCT_DETAIL,
          TRACKING_PAGES.PRODUCT_DETAIL
        ),
        index: 1,
        section_name: TRACKING_PAGES.PRODUCT_DETAIL,
        source: 'detail',
      });
    }
    onOpenContactSales();
  };

  return (
    <Box display={{ base: 'none', md: 'flex' }} justifyContent="flex-end">
      <Box shadow="base" w="full" rounded="lg" p="4" minW="300px">
        <Text fontSize="xl" fontWeight="bold">
          Atur Jumlah Pesanan
        </Text>
        {!isOutOfStock && !isPriceZero && !(isFnBEquipment && isOutOfStock) ? (
          <Box mt="2">
            <QuantityControl
              variant="add-to-cart"
              hasFractionalStep={hasFractionalStep}
              inputProps={inputProps}
              incProps={incProps}
              decProps={decProps}
              isLoading={isCartTotalFetching}
            />
          </Box>
        ) : null}
        <Box>
          <Text
            color={
              isPriceZero || (isFnBEquipment && isOutOfStock)
                ? 'blue'
                : statusColor
            }
            fontWeight="semibold"
          >
            {isPriceZero || (isFnBEquipment && isOutOfStock)
              ? 'Hubungi Sales'
              : available}
          </Text>
          <Text fontSize="xl" fontWeight="bold" pt="4">
            Total
          </Text>
          {isMagnifiedPriceDiff && cartTotalData ? (
            <Box mb="1">
              <Text as="span" color="gray.75" textDecoration="line-through">
                {buyerCurrencyFormatter(
                  cartTotalData.originalPriceMagnify.magnifiedValue
                )}
              </Text>
              <Text as="span" ml="2" color="#FF0000">
                Hemat{' '}
                {buyerCurrencyFormatter(
                  cartTotalData.savedPriceMagnify.magnifiedValue
                )}
              </Text>
            </Box>
          ) : null}
          <Skeleton borderRadius="md" isLoaded={!isCartTotalFetching}>
            <Box py="2" px="4" flex="1" bgColor="gray.33" rounded="lg">
              <Text color="blue" fontWeight="bold" fontSize="2xl">
                {isPriceZero || !cartTotalData
                  ? '-'
                  : buyerCurrencyFormatter(
                      cartTotalData.finalPriceMagnify.magnifiedValue
                    )}
              </Text>
            </Box>
          </Skeleton>
        </Box>
        <VStack spacing="2" w="full" mt="8">
          <Button
            w="full"
            bgColor="blue"
            color="white"
            leftIcon={
              (!isOutOfStock ||
                isPriceZero ||
                (isFnBEquipment && isOutOfStock)) && <Add fontSize="small" />
            }
            variant="solid"
            id="button-add-cart"
            onClick={() => {
              if (isPriceZero || (isFnBEquipment && isOutOfStock)) {
                handleContactSalesClick();
                return;
              }
              if (Number(count) <= 0) {
                showToast(
                  toast,
                  'error',
                  'Jumlah pesanan tidak boleh 0(nol) atau kosong'
                );
              } else {
                handleAddToCart(convertToFloat(count));
              }
            }}
            isDisabled={
              (!isPriceZero &&
                !(isFnBEquipment && isOutOfStock) &&
                isOutOfStock) ||
              minOrderQty > maxStock
            }
            isLoading={isCartTotalFetching && !isPriceZero}
          >
            {isOutOfStock && !isPriceZero && !(isFnBEquipment && isOutOfStock)
              ? 'Stock Habis'
              : 'Keranjang'}
          </Button>
          <Button
            w="full"
            leftIcon={<Add fontSize="small" />}
            onClick={() => {
              if (isPriceZero || (isFnBEquipment && isOutOfStock)) {
                handleContactSalesClick();
                return;
              }
              if (Number(count) <= 0) {
                showToast(
                  toast,
                  'error',
                  'Jumlah pesanan tidak boleh 0(nol) atau kosong'
                );
              } else {
                handleAddToTemplate(convertToFloat(count));
              }
            }}
            isDisabled={
              (!isPriceZero &&
                !(isFnBEquipment && isOutOfStock) &&
                isOutOfStock) ||
              minOrderQty > maxStock
            }
          >
            Daftar Belanja
          </Button>
          <Button
            id="btn-add-to-wishlist"
            w="full"
            border="none"
            leftIcon={
              wishlistId ? (
                <Icon as={Favorite} color="red" />
              ) : (
                <Icon as={FavoriteBorder} color="gray.75" />
              )
            }
            onClick={handleAddToWishlist}
          >
            {wishlistId ? 'Dimasukan ke wishlist' : 'Wishlist'}
          </Button>
        </VStack>
        <ContactSalesModal
          isOpen={isContactSalesOpen}
          onClose={onCloseContactSales}
          productId={productId}
          source="detail"
        />
      </Box>
    </Box>
  );
};

type OrderQuantityCounterMobileProps = OrderQuantityCounterProps;

export const OrderQuantityCounterMobile = ({
  productId,
  step,
  minimumValue,
  precision,
  stock,
  stockStatus,
  handleAddToTemplate,
  handleAddToCart,
  handleAddToWishlist,
  setHasActiveDiscount,
  setProduct,
  cartTotalData,
  isCartTotalFetching,
  unitPrice = 0,
  productData,
  isFnBEquipment = false,
}: OrderQuantityCounterMobileProps) => {
  const [openCounter, setOpenCounter] = useState<boolean>(false);
  const [available, setAvailable] = useState<
    'Available' | 'Out of Stock' | 'Stok Terbatas' | 'Produk Belum Tersedia'
  >('Out of Stock');
  const [statusColor, setStatusColor] = useState<
    'azure' | 'red' | 'orange' | 'red'
  >('red');
  const [uiQuantity, setUiQuantity] = useState<number>(0);
  const toast = useToast();

  const orderQtyMultiple = useMemo(
    () =>
      Number(
        toDecimal(
          dinero({
            amount: step,
            currency: STOCK_UI,
          })
        )
      ),
    [step]
  );

  const minOrderQty = useMemo(
    () =>
      Number(
        toDecimal(
          dinero({
            amount: minimumValue,
            currency: STOCK_UI,
          })
        )
      ),
    [minimumValue]
  );

  const maxStock = useMemo(
    () =>
      Number(
        toDecimal(
          dinero({
            amount: stock,
            currency: STOCK_UI,
          })
        )
      ),
    [stock]
  );

  const maxStockGanjil = useMemo(
    () =>
      Number(
        (Math.floor(maxStock / orderQtyMultiple) * orderQtyMultiple).toFixed(2)
      ),
    [maxStock, orderQtyMultiple]
  );

  const maxStockGenap = useMemo(
    () =>
      Number(
        (Math.round(maxStock / orderQtyMultiple) * orderQtyMultiple).toFixed(2)
      ),
    [maxStock, orderQtyMultiple]
  );

  const maxAllowedQuantity = useMemo(
    () =>
      isEven(orderQtyMultiple) || orderQtyMultiple === 1
        ? maxStockGanjil
        : maxStockGenap,
    [isEven, orderQtyMultiple, maxStockGanjil, maxStockGenap]
  );

  const {
    inputProps,
    incProps,
    decProps,
    hasFractionalStep,
    getCurrentUiValue,
  } = useStockNumberInput({
    id: `input-qty-mobile-${productId}`,
    name: `input-qty-mobile-${productId}`,
    valueUi: uiQuantity || minOrderQty,
    stepUi: orderQtyMultiple,
    minUi: minOrderQty,
    maxUi: maxAllowedQuantity,
    stepMagnified: step,
    roundOnBlurOnly: true,
    isDisabled: minOrderQty > maxStock || stockStatus === 'OUT_OF_STOCK',
    onChangeMagnifiedImmediate: (magnified) => {
      setUiQuantity(magnified / 1000);
      setProduct({
        product_id: productId,
        quantity: magnified,
      });
    },
    onChangeMagnified: (magnified) => {
      setUiQuantity(magnified / 1000);
      setProduct({
        product_id: productId,
        quantity: magnified,
      });
    },
    onError: (errorMessage) => {
      showToast(toast, 'error', errorMessage);
    },
  });

  const count = getCurrentUiValue().toString();

  useEffect(() => {
    setProduct({
      product_id: productId,
      quantity: minimumValue,
    });
  }, [productId, minimumValue, setProduct]);

  const setCompiledPromo = useCartStore((state) => state.setCompiledPromo);
  const resetCart = useCartStore((state) => state.reset);

  const handleCartPromoUpdate = useCallback(() => {
    if (cartTotalData?.promos) {
      setCompiledPromo(cartTotalData.promos);
    }
  }, [cartTotalData?.promos, setCompiledPromo]);

  useEffect(() => {
    handleCartPromoUpdate();
    return () => {
      resetCart([]);
    };
  }, [handleCartPromoUpdate, resetCart]);

  const isPriceZero = unitPrice === 0;
  // Only out of stock is truly unavailable. Price zero shows contact sales flow.
  const isOutOfStock = maxStock <= 0;

  const availableStatus = useMemo(() => {
    const available:
      | 'Available'
      | 'Out of Stock'
      | 'Stok Terbatas'
      | 'Produk Belum Tersedia' = isPriceZero
      ? 'Produk Belum Tersedia'
      : maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'Available'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'Stok Terbatas'
      : 'Out of Stock';

    const color: 'azure' | 'red' | 'orange' = isPriceZero
      ? 'red'
      : maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'azure'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'orange'
      : 'red';

    return { available, color };
  }, [maxStock, stockStatus, isPriceZero]);

  useEffect(() => {
    setAvailable(availableStatus.available);
    setStatusColor(availableStatus.color);
  }, [availableStatus]);

  const isMagnifiedPriceDiff = useMemo(
    () =>
      cartTotalData?.originalPriceMagnify?.magnifiedValue !==
      cartTotalData?.finalPriceMagnify?.magnifiedValue,
    [
      cartTotalData?.originalPriceMagnify?.magnifiedValue,
      cartTotalData?.finalPriceMagnify?.magnifiedValue,
    ]
  );

  useEffect(() => {
    setHasActiveDiscount?.(isMagnifiedPriceDiff);
  }, [isMagnifiedPriceDiff, setHasActiveDiscount]);

  // Contact Sales Modal for price zero products (mobile)
  const {
    isOpen: isContactSalesOpen,
    onOpen: onOpenContactSales,
    onClose: onCloseContactSales,
  } = useDisclosure();

  const handleContactSalesClick = () => {
    trackEvent(TRACKING_EVENTS.CONTACT_SALES_CTA_CLICK, {
      product_id: productId,
      source: 'detail-mobile',
    });
    if (productData) {
      trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
        ...getItemCardTrackingParams(
          productData,
          1,
          TRACKING_PAGES.PRODUCT_DETAIL,
          TRACKING_PAGES.PRODUCT_DETAIL
        ),
        index: 1,
        section_name: TRACKING_PAGES.PRODUCT_DETAIL,
        source: 'detail-mobile',
      });
    }
    onOpenContactSales();
  };

  return (
    <>
      <Card
        display={{ base: openCounter ? 'flex' : 'none', md: 'none' }}
        width="92%"
        justifyContent="center"
        position="fixed"
        bgColor="white"
        bottom="20"
        zIndex={1}
      >
        <CardBody>
          <Text fontSize="lg" fontWeight="bold">
            Atur Jumlah Pesanan
          </Text>
          <Box my="4">
            <QuantityControl
              variant="add-to-cart"
              hasFractionalStep={hasFractionalStep}
              inputProps={inputProps}
              incProps={incProps}
              decProps={decProps}
              isLoading={isCartTotalFetching}
            />
          </Box>
          <Box>
            <Text
              color={
                isPriceZero || (isFnBEquipment && isOutOfStock)
                  ? 'blue'
                  : statusColor
              }
              fontWeight="semibold"
            >
              {isPriceZero || (isFnBEquipment && isOutOfStock)
                ? 'Hubungi Sales'
                : available}
            </Text>
            <Text fontSize="lg" pt="4">
              Total
            </Text>
            {isMagnifiedPriceDiff ? (
              <Box mb="1">
                <Text as="span" color="gray.75" textDecoration="line-through">
                  {buyerCurrencyFormatter(
                    cartTotalData.originalPriceMagnify.magnifiedValue
                  )}
                </Text>
                <Text as="span" ml="2" color="#FF0000">
                  Hemat{' '}
                  {buyerCurrencyFormatter(
                    cartTotalData.savedPriceMagnify.magnifiedValue
                  )}
                </Text>
              </Box>
            ) : null}
            <Skeleton borderRadius="md" isLoaded={!isCartTotalFetching}>
              <Box py="2" px="4" flex="1" bgColor="gray.33" rounded="lg">
                <Text color="blue" fontWeight="bold" fontSize="lg">
                  {isPriceZero || !cartTotalData
                    ? '-'
                    : buyerCurrencyFormatter(
                        cartTotalData.finalPriceMagnify.magnifiedValue
                      )}
                </Text>
              </Box>
            </Skeleton>
          </Box>
        </CardBody>
      </Card>
      <Box
        display={{ base: 'flex', md: 'none' }}
        width="92%"
        position="fixed"
        bgColor="white"
        bottom="0"
        backgroundColor="white"
        zIndex={1}
      >
        <VStack spacing="2" w="full" pb="8" pt="2">
          <HStack spacing="2" w="full">
            <Button
              w="full"
              leftIcon={<Add fontSize="small" />}
              onClick={() => {
                if (isPriceZero || (isFnBEquipment && isOutOfStock)) {
                  handleContactSalesClick();
                  return;
                }
                if (Number(count) <= 0) {
                  showToast(
                    toast,
                    'error',
                    'Jumlah pesanan tidak boleh 0(nol) atau kosong'
                  );
                } else {
                  handleAddToTemplate(convertToFloat(count));
                }
              }}
              isDisabled={
                (!isPriceZero &&
                  !(isFnBEquipment && isOutOfStock) &&
                  isOutOfStock) ||
                minOrderQty > maxStock
              }
            >
              Daftar Belanja
            </Button>
            <Button
              w="full"
              bgColor="blue"
              color="white"
              variant="solid"
              id="button-add-cart"
              leftIcon={
                (!isOutOfStock ||
                  isPriceZero ||
                  (isFnBEquipment && isOutOfStock)) && <Add fontSize="small" />
              }
              onClick={() => {
                if (isPriceZero || (isFnBEquipment && isOutOfStock)) {
                  handleContactSalesClick();
                  return;
                }
                if (Number(count) <= 0) {
                  showToast(
                    toast,
                    'error',
                    'Jumlah pesanan tidak boleh 0(nol) atau kosong'
                  );
                } else if (!openCounter) {
                  setOpenCounter(true);
                } else {
                  handleAddToCart(convertToFloat(count));
                }
              }}
              isDisabled={
                (!isPriceZero &&
                  !(isFnBEquipment && isOutOfStock) &&
                  isOutOfStock) ||
                minOrderQty > maxStock
              }
              isLoading={isCartTotalFetching && !isPriceZero}
            >
              {isOutOfStock && !isPriceZero && !(isFnBEquipment && isOutOfStock)
                ? 'Stock Habis'
                : 'Keranjang'}
            </Button>
          </HStack>
        </VStack>
      </Box>
      <ContactSalesModal
        isOpen={isContactSalesOpen}
        onClose={onCloseContactSales}
        productId={productId}
        source="detail"
      />
    </>
  );
};
